import React, { useEffect, useState, useContext, useMemo } from 'react';
import './AdminProducts.css';
import { LocaleContext, AuthContext } from '../locale';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

export default function AdminProducts() {
  const { locale } = useContext(LocaleContext);
  const { user, openAuth, token } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const nf = useMemo(
    () => new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' }),
    [locale]
  );

  // Protect: redirect non-admins (hook must not be conditional)
  useEffect(() => {
    if (!user) {
      openAuth && openAuth('login');
      window.location.hash = '#/';
    } else if (user.role !== 'admin') {
      window.location.hash = '#/';
    }
  }, [user, openAuth]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/products`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch products');
      setProducts(Array.isArray(data.products) ? data.products : []);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      [p.name, p.marque, p.category, p.type, p.country, p.gamme]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [products, search]);

  const handleEdit = (product) => {
    setEditProduct({ ...product });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    setError('');
    try {
      console.log('Updating product ID:', editProduct.id);
      
      // Fix 1: Ensure ID is URL encoded
      const encodedId = encodeURIComponent(editProduct.id);
      console.log('Encoded product ID:', encodedId);
      
      const res = await fetch(`${API_BASE}/products/${encodedId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editProduct.name,
          category: editProduct.category,
          marque: editProduct.marque,
          type: editProduct.type,
          color: editProduct.color,
          country: editProduct.country,
          gamme: editProduct.gamme,
          container: editProduct.container,
          price: parseFloat(editProduct.price),
          volume: editProduct.volume,
          abv: parseFloat(editProduct.abv),
          image_url: editProduct.image_url,
          description: editProduct.description,
          stock_quantity: parseInt(editProduct.stock_quantity) || 0
        })
      });
      
      // Add better error handling with response details
      if (!res.ok) {
        const text = await res.text();
        console.error('Server response:', text);
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch (e) {
          errorData = { message: text || 'Unknown error' };
        }
        throw new Error(errorData.message || `Failed to update product (${res.status})`);
      }
      
      const data = await res.json();
      console.log('Update successful, received data:', data);
      
      // Update local state
      setProducts(products.map(p => p.id === editProduct.id ? data.product : p));
      setEditModal(false);
      setEditProduct(null);
    } catch (e) {
      console.error('Error updating product:', e);
      setError(e.message || 'Error updating product');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    setError('');
    try {
      console.log('Deleting product ID:', productId);
      
      // Fix 1: Ensure ID is URL encoded
      const encodedId = encodeURIComponent(productId);
      console.log('Encoded product ID:', encodedId);
      
      const res = await fetch(`${API_BASE}/products/${encodedId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Add better error handling with response details
      if (!res.ok) {
        const text = await res.text();
        console.error('Server response:', text);
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch (e) {
          errorData = { message: text || 'Unknown error' };
        }
        throw new Error(errorData.message || `Failed to delete product (${res.status})`);
      }
      
      const data = await res.json();
      
      // Remove from local state
      setProducts(products.filter(p => p.id !== productId));
      setDeleteConfirm(null);
    } catch (e) {
      console.error('Error deleting product:', e);
      setError(e.message || 'Error deleting product');
    }
  };

  const allowed = !!user && user.role === 'admin';
  if (!allowed) return null;

  return (
    <section className="admin-products">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <button
              className="back-btn"
              onClick={() => (window.location.hash = '#/admin')}
              title="Retour au dashboard"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="header-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </div>
            <div className="header-text">
              <h1 className="h1">Catalogue produits</h1>
              <p className="page-subtitle">{products.length} produit(s) actifs</p>
            </div>
          </div>

          <div className="header-actions">
            <input
              className="search-input"
              type="text"
              placeholder="Rechercher par nom, marque, type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn secondary" onClick={fetchProducts} disabled={loading} title="Rafraîchir">
              {loading ? (
                <>
                  <div className="spinner" />
                  Chargement...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 8 }}>
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" />
                    <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14" />
                  </svg>
                  Rafraîchir
                </>
              )}
            </button>
            <button
              className="btn primary"
              onClick={() => (window.location.hash = '#/admin/add-product')}
              title="Ajouter un produit"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 8 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              Ajouter
            </button>
          </div>
        </div>

        <div className="products-content">
          {error && <div className="alert error">{error}</div>}

          {loading ? (
            <div className="centered">
              <div className="spinner-lg" />
              <span>Chargement des produits...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">🍺🍷</div>
              <h3>Aucun produit</h3>
              <p>Essayez d'ajouter un produit ou de modifier votre recherche.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Nom</th>
                    <th>Marque</th>
                    <th>Catégorie</th>
                    <th>Type</th>
                    <th>Volume</th>
                    <th>ABV</th>
                    <th>Prix</th>
                    <th>Stock</th>
                    <th>Pays</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const emoji = p.category === 'beer' ? '🍺' : '🍷';
                    const stockCls = p.stock_quantity <= 0 ? 'stock-badge out' : p.stock_quantity < 10 ? 'stock-badge low' : 'stock-badge ok';
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="thumb-small">
                            {p.image_url ? (
                              <img
                                src={p.image_url}
                                alt={p.name}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement.querySelector('.thumb-fallback').style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="thumb-fallback" style={{ display: p.image_url ? 'none' : 'flex' }}>{emoji}</div>
                          </div>
                        </td>
                        <td className="cell-strong">{p.name}</td>
                        <td>{p.marque}</td>
                        <td className="cell-cap">{p.category}</td>
                        <td>{p.type}</td>
                        <td>{p.volume}</td>
                        <td>{Number(p.abv).toFixed(1)}%</td>
                        <td className="cell-strong">{nf.format(p.price)}</td>
                        <td><span className={stockCls}>{p.stock_quantity}</span></td>
                        <td>{p.country}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-action edit" 
                              onClick={() => handleEdit(p)}
                              title="Modifier"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button 
                              className="btn-action delete" 
                              onClick={() => setDeleteConfirm(p.id)}
                              title="Supprimer"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && editProduct && (
        <div className="modal-backdrop" onClick={() => setEditModal(false)}>
          <div className="modal edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier le produit</h3>
              <button className="modal-close" onClick={() => setEditModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nom</label>
                    <input
                      type="text"
                      value={editProduct.name}
                      onChange={(e) => setEditProduct({...editProduct, name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Marque</label>
                    <input
                      type="text"
                      value={editProduct.marque}
                      onChange={(e) => setEditProduct({...editProduct, marque: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Prix (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editProduct.price}
                      onChange={(e) => setEditProduct({...editProduct, price: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Stock</label>
                    <input
                      type="number"
                      value={editProduct.stock_quantity}
                      onChange={(e) => setEditProduct({...editProduct, stock_quantity: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <input
                      type="text"
                      value={editProduct.type}
                      onChange={(e) => setEditProduct({...editProduct, type: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Couleur</label>
                    <input
                      type="text"
                      value={editProduct.color}
                      onChange={(e) => setEditProduct({...editProduct, color: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Volume</label>
                    <input
                      type="text"
                      value={editProduct.volume}
                      onChange={(e) => setEditProduct({...editProduct, volume: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>ABV (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editProduct.abv}
                      onChange={(e) => setEditProduct({...editProduct, abv: e.target.value})}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>URL de l'image</label>
                    <input
                      type="url"
                      value={editProduct.image_url || ''}
                      onChange={(e) => setEditProduct({...editProduct, image_url: e.target.value})}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea
                      rows="3"
                      value={editProduct.description || ''}
                      onChange={(e) => setEditProduct({...editProduct, description: e.target.value})}
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn secondary" onClick={() => setEditModal(false)}>
                    Annuler
                  </button>
                  <button className="btn primary" onClick={handleSaveEdit} disabled={editLoading}>
                    {editLoading ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirmer la suppression</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer ce produit ?</p>
              <p className="warning">Cette action est irréversible.</p>
              <div className="modal-actions">
                <button className="btn secondary" onClick={() => setDeleteConfirm(null)}>
                  Annuler
                </button>
                <button className="btn danger" onClick={() => handleDelete(deleteConfirm)}>
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
